import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Bell, 
  Check,
  User,
  Calendar,
  Star,
  Phone,
  Briefcase,
  Megaphone,
  Filter,
  MarkAsRead
} from 'lucide-react';
import { demoNotifiche } from './demoData';

const DemoNotifiche = ({ userType }) => {
  const [notifiche, setNotifiche] = useState(demoNotifiche[userType] || []);
  const [filtro, setFiltro] = useState('tutte'); // tutte, non_lette, lette

  const getIconaNotifica = (icona, colore) => {
    const iconMap = {
      'user-plus': <User className="w-5 h-5" />,
      'calendar-check': <Calendar className="w-5 h-5" />,
      'star': <Star className="w-5 h-5" />,
      'phone': <Phone className="w-5 h-5" />,
      'briefcase': <Briefcase className="w-5 h-5" />,
      'megaphone': <Megaphone className="w-5 h-5" />
    };
    
    const colorMap = {
      'blue': 'text-blue-600 bg-blue-100',
      'green': 'text-green-600 bg-green-100', 
      'yellow': 'text-yellow-600 bg-yellow-100',
      'purple': 'text-purple-600 bg-purple-100',
      'red': 'text-red-600 bg-red-100'
    };
    
    return (
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorMap[colore] || 'text-gray-600 bg-gray-100'}`}>
        {iconMap[icona] || <Bell className="w-5 h-5" />}
      </div>
    );
  };

  const handleSegnaComeLetta = (id) => {
    setNotifiche(prev => 
      prev.map(notifica => 
        notifica.id === id ? { ...notifica, letta: true } : notifica
      )
    );
  };

  const handleSegnaTutteComeLette = () => {
    setNotifiche(prev => 
      prev.map(notifica => ({ ...notifica, letta: true }))
    );
  };

  const filteredNotifiche = notifiche.filter(notifica => {
    switch (filtro) {
      case 'non_lette': return !notifica.letta;
      case 'lette': return notifica.letta;
      default: return true;
    }
  });

  const notificheNonLette = notifiche.filter(n => !n.letta).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-900">Notifiche</h2>
          {notificheNonLette > 0 && (
            <Badge className="bg-red-500 text-white">
              {notificheNonLette} nuove
            </Badge>
          )}
        </div>
        
        {notificheNonLette > 0 && (
          <Button
            onClick={handleSegnaTutteComeLette}
            variant="outline"
            size="sm"
            className="rounded-xl"
          >
            <Check className="w-4 h-4 mr-2" />
            Segna tutte come lette
          </Button>
        )}
      </div>

      {/* Filtri */}
      <div className="flex gap-2">
        {[
          { key: 'tutte', label: 'Tutte', icon: Bell },
          { key: 'non_lette', label: 'Non lette', icon: Bell },
          { key: 'lette', label: 'Lette', icon: Check }
        ].map((filterItem) => {
          const IconComponent = filterItem.icon;
          return (
            <Button
              key={filterItem.key}
              variant={filtro === filterItem.key ? "default" : "outline"}
              onClick={() => setFiltro(filterItem.key)}
              className={`flex items-center gap-2 rounded-xl ${
                filtro === filterItem.key
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                  : 'hover:bg-blue-50'
              }`}
              size="sm"
            >
              <IconComponent className="w-4 h-4" />
              {filterItem.label}
            </Button>
          );
        })}
      </div>

      {/* Lista Notifiche */}
      <div className="space-y-3">
        {filteredNotifiche.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna notifica per questo filtro.</p>
            </div>
          </Card>
        ) : (
          filteredNotifiche.map((notifica) => (
            <Card 
              key={notifica.id} 
              className={`hover:shadow-md transition-shadow ${
                !notifica.letta ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {getIconaNotifica(notifica.icona, notifica.colore)}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className={`font-semibold mb-1 ${
                          !notifica.letta ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {notifica.titolo}
                        </h4>
                        <p className={`text-sm ${
                          !notifica.letta ? 'text-gray-700' : 'text-gray-600'
                        }`}>
                          {notifica.messaggio}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {notifica.timestamp}
                        </span>
                        {!notifica.letta && (
                          <Button
                            onClick={() => handleSegnaComeLetta(notifica.id)}
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 rounded-full hover:bg-blue-100"
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Badge Tipo */}
                    <div className="flex items-center gap-2">
                      <Badge 
                        className={`text-xs px-2 py-1 ${
                          notifica.colore === 'blue' ? 'bg-blue-100 text-blue-800' :
                          notifica.colore === 'green' ? 'bg-green-100 text-green-800' :
                          notifica.colore === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                          notifica.colore === 'purple' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {notifica.tipo.replace('_', ' ')}
                      </Badge>
                      
                      {!notifica.letta && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{notifiche.length}</div>
            <div className="text-sm opacity-90">Totale notifiche</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{notificheNonLette}</div>
            <div className="text-sm opacity-90">Non lette</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{notifiche.length - notificheNonLette}</div>
            <div className="text-sm opacity-90">Gestite</div>
          </CardContent>
        </Card>
      </div>

      {/* Demo Info */}
      <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
        <div className="flex items-start gap-2">
          <Bell className="w-5 h-5 text-purple-600 mt-0.5" />
          <div className="text-sm text-purple-700">
            <strong>Demo Notifiche:</strong> Le notifiche mostrate sono esempi realistici di come 
            {userType === 'agenzia' ? ' le agenzie ricevono updates su candidati, colloqui e feedback' : ' i candidati vengono aggiornati su opportunità, contatti e novità nella loro zona'}.
            Clicca "Segna come letta" per simulare l'interazione.
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoNotifiche;